import { ConfirmModal } from '@sparklayer-io/spark-dash-ui/components/confirm-modal';
import { useModal } from '@sparklayer-io/spark-dash-ui/hooks/use-modal';
import { Page } from '@ui/themed';
import { t } from 'i18next';
import { useCallback, useEffect, useRef } from 'react';
import { SparkSuspense } from '../../../components/spark-suspense';
import { isLive, useEnv } from '../../../context/env-context';
import { useUser, withPageProviders } from '../../../context/page-context';
import type { I18nPrefix } from '../../../i18n/prefix';
import { AnalyticsCard } from '../components/cards/analytics-card';
import { CheckoutSettingsCard } from '../components/cards/checkout-settings-card';
import { MyAccountSettingsCard } from '../components/cards/my-account-settings-card';
import { PricingSettingsCard } from '../components/cards/pricing-settings-card';
import { PublishStatusCard } from '../components/cards/publish-status-card';
import { SlotsCard } from '../components/cards/slots-card';
import { TranslationOverridesCard } from '../components/cards/translation-overrides-card';
import { ImportExportCard } from '../components/import-export-card';
import { StorefrontOptionsProvider } from '../context/storefront-options-context';
import { useStorefrontOptions } from '../hooks/use-storefront-options';
import { useUnsavedChangesWarning } from '../hooks/use-unsaved-changes-warning';

const PAGE = 'features:storefront_options.pages.storefront_options_page' satisfies I18nPrefix;

export type StorefrontOptionsPageProps = {
  onDirtyChange?: (isDirty: boolean) => void;
};

export const StorefrontOptionsPage = withPageProviders<StorefrontOptionsPageProps>(
  StorefrontOptionsPageContent,
);

function StorefrontOptionsPageContent({ onDirtyChange }: StorefrontOptionsPageProps) {
  const controls = useStorefrontOptions();
  const { isSparkStaff } = useUser();
  const env = useEnv();

  const {
    isDirty,
    isSaving,
    isBusy,
    canResetToDefaults,
    canDelete,
    resetToDefaults,
    deleteSettings,
  } = controls;

  const {
    isOpen: isPublishModalOpen,
    openModal: openPublishModal,
    closeModal: closePublishModal,
  } = useModal();

  const {
    isOpen: isDiscardModalOpen,
    openModal: openDiscardModal,
    closeModal: closeDiscardModal,
  } = useModal();

  const {
    isOpen: isResetModalOpen,
    openModal: openResetModal,
    closeModal: closeResetModal,
  } = useModal();

  const {
    isOpen: isDeleteModalOpen,
    openModal: openDeleteModal,
    closeModal: closeDeleteModal,
  } = useModal();

  const slotsRef = useRef<HTMLDivElement>(null);

  useUnsavedChangesWarning(isDirty);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const revealSlots = useCallback(() => {
    slotsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, []);

  const publishModalBody = isLive(env.environment) ? 'body_live' : 'body_test';

  function handlePublish() {
    controls.saveAndPublish();
    closePublishModal();
  }

  function handleDiscard() {
    controls.discardChanges();
    closeDiscardModal();
  }

  function handleReset() {
    resetToDefaults();
    closeResetModal();
  }

  function handleDelete() {
    deleteSettings();
    closeDeleteModal();
  }

  return (
    <Page
      title={t(`${PAGE}.header.title`)}
      description={t(`${PAGE}.header.description`)}
      learnMoreUrl="https://docs.sparklayer.io/frontend-integration"
      primaryAction={{
        label: t(`${PAGE}.header.save`),
        onClick: openPublishModal,
        disabled: !isDirty || isBusy,
        loading: isSaving,
      }}
      secondaryActions={[
        {
          label: t(`${PAGE}.header.secondary_actions.discard`),
          icon: 'x',
          onClick: openDiscardModal,
          disabled: !isDirty || isBusy,
        },
        {
          label: t(`${PAGE}.header.secondary_actions.reset`),
          icon: 'refresh',
          onClick: openResetModal,
          disabled: !canResetToDefaults || isBusy,
        },
        ...(isSparkStaff
          ? [
              {
                label: t(`${PAGE}.header.secondary_actions.delete`),
                icon: 'delete' as const,
                onClick: openDeleteModal,
                disabled: !canDelete || isBusy,
              },
            ]
          : []),
      ]}
      narrow
    >
      <SparkSuspense
        isLoading={controls.isLoading}
        isError={controls.isError}
        onReload={controls.refetch}
        errorMessage={t('errors:generic')}
      >
        <StorefrontOptionsProvider value={controls}>
          <PublishStatusCard />
          <ImportExportCard />
          <CheckoutSettingsCard />
          <MyAccountSettingsCard onRevealSlots={revealSlots} />
          <TranslationOverridesCard />
          <PricingSettingsCard />
          <div ref={slotsRef}>
            <SlotsCard />
          </div>
          <AnalyticsCard />
        </StorefrontOptionsProvider>
      </SparkSuspense>

      <ConfirmModal
        isOpen={isPublishModalOpen}
        onClose={closePublishModal}
        onConfirm={handlePublish}
        danger={false}
        title={t(`${PAGE}.publish_modal.title`)}
        confirmText={t(`${PAGE}.publish_modal.confirm`)}
        message={t(`${PAGE}.publish_modal.${publishModalBody}`)}
      />

      <ConfirmModal
        isOpen={isDiscardModalOpen}
        onClose={closeDiscardModal}
        onConfirm={handleDiscard}
        title={t(`${PAGE}.discard_modal.title`)}
        confirmText={t(`${PAGE}.discard_modal.confirm`)}
        message={t(`${PAGE}.discard_modal.body`)}
      />

      <ConfirmModal
        isOpen={isResetModalOpen}
        onClose={closeResetModal}
        onConfirm={handleReset}
        title={t(`${PAGE}.reset_modal.title`)}
        confirmText={t(`${PAGE}.reset_modal.confirm`)}
        message={t(`${PAGE}.reset_modal.body`)}
      />

      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title={t(`${PAGE}.delete_modal.title`)}
        confirmText={t(`${PAGE}.delete_modal.confirm`)}
        message={t(`${PAGE}.delete_modal.body`)}
      />
    </Page>
  );
}
